import { tr } from "../i18n/LanguageContext";
import React, { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Link, useNavigate } from "../i18n/router";
import { Menu, X, User, LogIn, LogOut, Settings, Shield, ChevronDown, MessageCircle, Bell, Calendar, CreditCard } from "lucide-react";
import { Button } from "./ui/button";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage, stripLang } from "../i18n/LanguageContext";
import { LanguageSwitcher } from "./LanguageSwitcher";

const roleLabelMap = {
  admin: "Администратор",
  organizer: "Организатор",
  participant: "Участник",
};

const roleBgMap = {
  admin: "#dbeafe",
  organizer: "var(--eunoia-accent-whisper)",
  participant: "#f3f4f6",
};

const roleColorMap = {
  admin: "#1d4ed8",
  organizer: "var(--eunoia-accent)",
  participant: "#6b7280",
};

const Navbar = () => {
  const { t, localePath } = useLanguage();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [myEventsCount, setMyEventsCount] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading, logout } = useAuth();
  const profileRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
    setIsProfileOpen(false);
  }, [location.pathname]);

  // Fetch unread message count
  useEffect(() => {
    if (!user) { setUnreadCount(0); setMyEventsCount(0); return; }
    const fetchUnread = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/messages/unread-count`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setUnreadCount(data.count || 0);
        }
      } catch { /* ignore */ }
    };
    const fetchMyEvents = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/my-events`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          // Count upcoming events where user is confirmed/requested/paid (excludes 'interested')
          const active = (data.upcoming || []).filter(
            (e) => ["confirmed", "requested", "paid"].includes(e.myStatus)
          );
          setMyEventsCount(active.length);
        }
      } catch { /* ignore */ }
    };
    fetchUnread();
    fetchMyEvents();
    const interval = setInterval(() => { fetchUnread(); fetchMyEvents(); }, 30000); // poll every 30s
    return () => clearInterval(interval);
  }, [user]);

  const navigationItems = [
    { name: t("nav.home"), path: "/" },
    { name: t("nav.events"), path: "/events" },
    { name: t("nav.organizers"), path: "/organizers" },
    { name: t("nav.addEvent"), path: "/add-event", authRequired: true },
    { name: t("nav.blog"), path: "/blog" },
    { name: t("nav.about"), path: "/about" }
  ];

  const isActive = (path) => stripLang(location.pathname) === path;

  const handleLogout = async () => {
    setIsProfileOpen(false);
    setIsMenuOpen(false);
    await logout();
  };

  const userRole = user?.role || "participant";

  return (
    <nav className="eunoia-header" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to={localePath("/")} className="flex items-center group" data-testid="navbar-logo">
            <div className="transition-all duration-300 group-hover:transform group-hover:scale-105">
              <h1 className="eunoia-brand-name">E U N O I A</h1>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            {navigationItems.map((item) => {
              const href = item.authRequired && !user
                ? `${localePath("/login")}?redirect=${localePath(item.path)}&intent=${item.path === "/add-event" ? "create_event" : "access"}`
                : localePath(item.path);

              return (
                <Link
                  key={item.name}
                  to={href}
                  data-testid={`nav-link-${item.path.replace(/\//g, '') || 'home'}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                    isActive(item.path)
                      ? "eunoia-btn-secondary bg-opacity-60"
                      : "text-gray-600 hover:bg-white/50 hover:text-gray-800"
                  }`}
                >
                  {item.name}
                </Link>
              );
            })}

            <div className="ml-3"><LanguageSwitcher /></div>

            {/* Auth Section */}
            <div className="ml-4 pl-4 border-l flex items-center gap-2" style={{borderColor: 'var(--eunoia-border)'}}>
              {loading ? (
                <div className="w-8 h-8 border-2 border-gray-300 border-t-amber-600 rounded-full animate-spin" />
              ) : user ? (
                <>
                  {/* Notifications Bell */}
                  <Link to="/notifications" data-testid="notifications-bell" className="relative p-2 rounded-full hover:bg-white/50 transition-colors">
                    <Bell className="h-5 w-5" style={{color: 'var(--eunoia-soft)'}} />
                    {unreadCount > 0 && (
                      <span className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full text-[9px] font-bold flex items-center justify-center text-white" style={{backgroundColor: 'var(--eunoia-accent)'}}>{unreadCount > 9 ? '9+' : unreadCount}</span>
                    )}
                  </Link>

                <div className="relative" ref={profileRef}>
                  <button
                    data-testid="profile-menu-trigger"
                    onClick={() => setIsProfileOpen(!isProfileOpen)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-300 ${
                      isActive("/profile") || isProfileOpen
                        ? "eunoia-btn-secondary bg-opacity-60"
                        : "text-gray-600 hover:bg-white/50 hover:text-gray-800"
                    }`}
                  >
                    {user.avatarUrl ? (
                      <img
                        src={user.avatarUrl}
                        alt={user.displayName}
                        className="w-6 h-6 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-6 h-6 rounded-full flex items-center justify-center"
                           style={{backgroundColor: 'var(--eunoia-accent-whisper)'}}>
                        <User className="h-3.5 w-3.5" style={{color: 'var(--eunoia-accent)'}} />
                      </div>
                    )}
                    <span className="max-w-[120px] truncate">{user.displayName || tr("Профиль")}</span>
                    <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${isProfileOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Profile Dropdown */}
                  {isProfileOpen && (
                    <div
                      data-testid="profile-dropdown"
                      className="absolute right-0 mt-2 w-64 rounded-xl shadow-lg py-1 z-50"
                      style={{backgroundColor: 'white', border: '1px solid var(--eunoia-whisper)'}}
                    >
                      {/* User Info Header */}
                      <div className="px-4 py-3 border-b" style={{borderColor: 'var(--eunoia-whisper)'}}>
                        <p data-testid="profile-dropdown-name" className="text-sm font-semibold truncate" style={{color: 'var(--eunoia-deep)'}}>
                          {user.displayName || tr("Пользователь")}
                        </p>
                        <p data-testid="profile-dropdown-email" className="text-xs mt-0.5 truncate" style={{color: 'var(--eunoia-soft)'}}>
                          {user.email}
                        </p>
                        <span
                          data-testid="profile-dropdown-role"
                          className="inline-flex items-center gap-1 mt-2 px-2 py-0.5 rounded-full text-xs font-medium"
                          style={{
                            backgroundColor: roleBgMap[userRole],
                            color: roleColorMap[userRole],
                          }}
                        >
                          <Shield className="h-3 w-3" />
                          {tr(roleLabelMap[userRole])}
                        </span>
                      </div>

                      {/* Menu Items */}
                      <div className="py-1">
                        <Link
                          to="/profile"
                          data-testid="profile-dropdown-settings"
                          className="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors"
                          style={{color: 'var(--eunoia-deep)'}}
                          onClick={() => setIsProfileOpen(false)}
                        >
                          <Settings className="h-4 w-4" />
                          {tr("Настройки профиля")}
                        </Link>

                        {userRole === 'admin' && (
                          <Link
                            to="/admin"
                            data-testid="profile-dropdown-admin"
                            className="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors"
                            style={{color: 'var(--eunoia-deep)'}}
                            onClick={() => setIsProfileOpen(false)}
                          >
                            <Shield className="h-4 w-4" />
                            {tr("Панель админа")}
                          </Link>
                        )}

                        <Link
                          to="/my-events"
                          data-testid="profile-dropdown-my-events"
                          className="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors"
                          style={{color: 'var(--eunoia-deep)'}}
                          onClick={() => setIsProfileOpen(false)}
                        >
                          <Calendar className="h-4 w-4" />
                          {tr("Мои события")}
                          {myEventsCount > 0 && (
                            <span className="ml-auto px-1.5 py-0.5 rounded-full text-[10px] font-bold text-white" style={{backgroundColor: 'var(--eunoia-accent)'}}>{myEventsCount}</span>
                          )}
                        </Link>

                        {(userRole === 'organizer' || userRole === 'admin') && (
                          <Link
                            to="/organizer/payments"
                            data-testid="profile-dropdown-payments"
                            className="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors"
                            style={{color: 'var(--eunoia-deep)'}}
                            onClick={() => setIsProfileOpen(false)}
                          >
                            <CreditCard className="h-4 w-4" />
                            {tr("Платежи")}
                            <span className="ml-auto px-1.5 py-0.5 rounded-full text-[9px] font-medium uppercase tracking-wider" style={{backgroundColor: 'var(--eunoia-whisper)', color: 'var(--eunoia-soft)'}}>{tr("Скоро")}</span>
                          </Link>
                        )}

                        <Link
                          to="/messages"
                          data-testid="profile-dropdown-messages"
                          className="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors"
                          style={{color: 'var(--eunoia-deep)'}}
                          onClick={() => setIsProfileOpen(false)}
                        >
                          <MessageCircle className="h-4 w-4" />
                          {tr("Сообщения")}
                          {unreadCount > 0 && (
                            <span className="ml-auto px-1.5 py-0.5 rounded-full text-[10px] font-bold text-white" style={{backgroundColor: 'var(--eunoia-accent)'}}>{unreadCount}</span>
                          )}
                        </Link>
                      </div>

                      {/* Logout */}
                      <div className="border-t py-1" style={{borderColor: 'var(--eunoia-whisper)'}}>
                        <button
                          data-testid="logout-button"
                          onClick={handleLogout}
                          className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors text-left"
                        >
                          <LogOut className="h-4 w-4" />
                          {tr("Выйти")}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                </>
              ) : (
                <Link
                  to="/login"
                  data-testid="login-button"
                  className={`flex items-center gap-2 eunoia-btn-primary text-sm ${
                    isActive("/login") ? "bg-opacity-80" : ""
                  }`}
                >
                  <LogIn className="h-4 w-4" />
                  <span>{tr("Войти")}</span>
                </Link>
              )}
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center gap-2">
            <LanguageSwitcher />
            <Button
              variant="ghost"
              size="sm"
              data-testid="mobile-menu-toggle"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-gray-600 hover:text-emerald-600"
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div className="md:hidden absolute top-16 left-0 right-0 eunoia-card-white shadow-lg z-50" style={{background: 'rgba(250, 247, 242, 0.98)', backdropFilter: 'saturate(140%) blur(8px)'}}>
          <div className="px-2 pt-2 pb-3 space-y-1">
            {navigationItems.map((item) => {
              const href = item.authRequired && !user
                ? `${localePath("/login")}?redirect=${localePath(item.path)}&intent=${item.path === "/add-event" ? "create_event" : "access"}`
                : localePath(item.path);

              return (
                <Link
                  key={item.name}
                  to={href}
                  onClick={() => setIsMenuOpen(false)}
                  className={`block px-3 py-2 rounded-lg text-base font-medium transition-all duration-300 ${
                    isActive(item.path)
                      ? "eunoia-btn-secondary bg-opacity-60"
                      : "text-gray-600 hover:bg-white/50 hover:text-gray-800"
                  }`}
                >
                  {item.name}
                </Link>
              );
            })}

            {/* Mobile Auth Section */}
            <div className="pt-3 mt-3" style={{borderTop: '1px solid var(--eunoia-border)'}}>
              {loading ? (
                <div className="flex justify-center py-2">
                  <div className="w-6 h-6 border-2 border-gray-300 border-t-amber-600 rounded-full animate-spin" />
                </div>
              ) : user ? (
                <div className="space-y-1">
                  {/* User Info */}
                  <div className="px-3 py-2" data-testid="mobile-user-info">
                    <p className="text-sm font-semibold" style={{color: 'var(--eunoia-deep)'}}>
                      {user.displayName || tr("Пользователь")}
                    </p>
                    <p className="text-xs" style={{color: 'var(--eunoia-soft)'}}>
                      {user.email}
                    </p>
                    <span
                      data-testid="mobile-role-badge"
                      className="inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded-full text-xs font-medium"
                      style={{
                        backgroundColor: roleBgMap[userRole],
                        color: roleColorMap[userRole],
                      }}
                    >
                      <Shield className="h-3 w-3" />
                      {tr(roleLabelMap[userRole])}
                    </span>
                  </div>

                  <Link
                    to="/profile"
                    onClick={() => setIsMenuOpen(false)}
                    data-testid="mobile-profile-link"
                    className="flex items-center gap-3 px-3 py-2 rounded-lg text-base font-medium text-gray-600 hover:bg-white/50"
                  >
                    <Settings className="h-5 w-5" />
                    {tr("Настройки профиля")}
                  </Link>

                  {userRole === 'admin' && (
                    <Link
                      to="/admin"
                      onClick={() => setIsMenuOpen(false)}
                      data-testid="mobile-admin-link"
                      className="flex items-center gap-3 px-3 py-2 rounded-lg text-base font-medium text-gray-600 hover:bg-white/50"
                    >
                      <Shield className="h-5 w-5" />
                      {tr("Панель админа")}
                    </Link>
                  )}

                  <Link
                    to="/my-events"
                    onClick={() => setIsMenuOpen(false)}
                    data-testid="mobile-my-events-link"
                    className="flex items-center gap-3 px-3 py-2 rounded-lg text-base font-medium text-gray-600 hover:bg-white/50"
                  >
                    <Calendar className="h-5 w-5" />
                    {tr("Мои события")}
                    {myEventsCount > 0 && (
                      <span className="ml-auto px-1.5 py-0.5 rounded-full text-[10px] font-bold text-white" style={{backgroundColor: 'var(--eunoia-accent)'}}>{myEventsCount}</span>
                    )}
                  </Link>

                  {(userRole === 'organizer' || userRole === 'admin') && (
                    <Link
                      to="/organizer/payments"
                      onClick={() => setIsMenuOpen(false)}
                      data-testid="mobile-payments-link"
                      className="flex items-center gap-3 px-3 py-2 rounded-lg text-base font-medium text-gray-600 hover:bg-white/50"
                    >
                      <CreditCard className="h-5 w-5" />
                      {tr("Платежи")}
                      <span className="ml-auto px-1.5 py-0.5 rounded-full text-[9px] font-medium uppercase tracking-wider" style={{backgroundColor: 'var(--eunoia-whisper)', color: 'var(--eunoia-soft)'}}>{tr("Скоро")}</span>
                    </Link>
                  )}

                  <Link
                    to="/messages"
                    onClick={() => setIsMenuOpen(false)}
                    data-testid="mobile-messages-link"
                    className="flex items-center gap-3 px-3 py-2 rounded-lg text-base font-medium text-gray-600 hover:bg-white/50"
                  >
                    <MessageCircle className="h-5 w-5" />
                    {tr("Сообщения")}
                    {unreadCount > 0 && (
                      <span className="ml-auto px-1.5 py-0.5 rounded-full text-[10px] font-bold text-white" style={{backgroundColor: 'var(--eunoia-accent)'}}>{unreadCount}</span>
                    )}
                  </Link>

                  <button
                    data-testid="mobile-logout-button"
                    onClick={handleLogout}
                    className="flex items-center gap-3 w-full px-3 py-2 rounded-lg text-base font-medium text-red-600 hover:bg-red-50 text-left"
                  >
                    <LogOut className="h-5 w-5" />
                    {tr("Выйти")}
                  </button>
                </div>
              ) : (
                <Link
                  to="/login"
                  onClick={() => setIsMenuOpen(false)}
                  data-testid="mobile-login-button"
                  className="flex items-center gap-3 eunoia-btn-primary text-base mx-3 mb-2"
                >
                  <LogIn className="h-5 w-5" />
                  <span>{tr("Войти")}</span>
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
